// New and edit client, with extra contacts.
import {
  CLIENT_TITLES,
  type ClientDetail,
  CONTACT_TYPE_WORDS,
  type ContactRow,
  type ContactType,
  EMAIL_TYPES,
  fullAddress,
  LEAD_SOURCES,
  PHONE_TYPES,
} from "@steward/shared";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, type SubmitEvent, useRef, useState } from "react";
import { useClient, useClients, useClientSummary, useCreateClient, useSaveProperty, useUpdateClient } from "../../api/hooks";
import { useSession } from "../../app/session";
import { Choice, Combobox, FormField, InputText } from "../../components/fields";
import { phoneText } from "../../components/format";
import { Icon } from "../../components/Icon";
import { markFresh } from "../../components/next";
import { useToast } from "../../components/overlays";
import { Banner, Button, IconButton, LoadError, Page, Skeleton, Switch } from "../../components/parts";
import { type AddressDraft, AddressFields, draftOf, partsOf } from "../../components/SubLocations";
import { errText } from "../../components/work";
import { LEAD_WORDS } from "./List";
import {
  EMPTY_PLACE,
  newClientMissing,
  type PlaceDraft,
  PlaceFields,
  placeOf,
  placeOut,
  placeProperty,
  propertyOut,
  txt,
} from "./Place";

/** What a phone or email is for: cell, office, billing. Phones and emails each offer their own list. */
export function ContactTypeSelect({
  kind,
  value,
  onChange,
}: {
  kind: "phone" | "email";
  value: ContactType;
  onChange: (v: ContactType) => void;
}) {
  return (
    <FormField label="Type">
      <select className="select" value={value} onChange={(e) => onChange(e.target.value as ContactType)}>
        {(kind === "phone" ? PHONE_TYPES : EMAIL_TYPES).map((t) => (
          <option key={t} value={t}>
            {CONTACT_TYPE_WORDS[t]}
          </option>
        ))}
      </select>
    </FormField>
  );
}

/** An extra phone or email on the client form. New ones have no id until saved. */
interface ContactDraft {
  key: string;
  id?: string;
  kind: "phone" | "email";
  type: ContactType;
  value: string;
  label: string;
}

export function contactDraftOf(x: ContactRow): ContactDraft {
  return {
    key: x.id,
    id: x.id,
    kind: x.kind,
    type: x.type ?? (x.kind === "phone" ? "cell" : "billing"),
    value: x.kind === "phone" ? (phoneText(x.value) ?? x.value) : x.value,
    label: x.label ?? "",
  };
}

/** The filled rows, ready to save, or what is wrong with one. */
export function contactsOut(list: ContactDraft[]): { contacts: Omit<ContactDraft, "key">[] } | { error: string } {
  const filled = list.filter((x) => x.value.trim());
  const bad = filled.find((x) =>
    x.kind === "email" ? !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x.value.trim()) : x.value.replace(/\D/g, "").length < 7,
  );
  if (bad) return { error: `The extra ${bad.kind === "email" ? "email" : "phone"} ${bad.value.trim()} does not look right.` };
  return {
    contacts: filled.map(({ key: _key, ...x }) => ({ ...x, value: x.value.trim(), label: x.label.trim() })),
  };
}

/** More phones and emails, each with its type and who it reaches, added and removed right on the form. */
function ExtraContacts({ list, set }: { list: ContactDraft[]; set: (v: ContactDraft[]) => void }) {
  const change = (key: string, p: Partial<ContactDraft>) => set(list.map((x) => (x.key === key ? { ...x, ...p } : x)));
  const add = (kind: "phone" | "email") =>
    set([...list, { key: crypto.randomUUID(), kind, type: kind === "phone" ? "office" : "billing", value: "", label: "" }]);
  return (
    <>
      {list.map((x) => (
        <div className="contact-row" key={x.key}>
          <ContactTypeSelect kind={x.kind} value={x.type} onChange={(type) => change(x.key, { type })} />
          <InputText
            label={x.kind === "phone" ? "Phone number" : "Email"}
            type={x.kind === "phone" ? "tel" : "email"}
            autoComplete="off"
            autoFocus={!x.id && !x.value}
            value={x.value}
            onChange={(e) => change(x.key, { value: e.target.value })}
          />
          <InputText
            label="Name"
            placeholder="Mike, front desk"
            autoComplete="off"
            maxLength={40}
            value={x.label}
            onChange={(e) => change(x.key, { label: e.target.value })}
          />
          <IconButton
            icon="trash"
            label={`Remove ${x.kind === "phone" ? "phone" : "email"}`}
            size={18}
            onClick={() => set(list.filter((y) => y.key !== x.key))}
          />
        </div>
      ))}
      <div className="span-6 row">
        <Button type="tertiary" size="small" icon="plus" onClick={() => add("phone")}>
          Add phone
        </Button>
        <Button type="tertiary" size="small" icon="plus" onClick={() => add("email")}>
          Add email
        </Button>
      </div>
    </>
  );
}

function FormSection({ title, intro, children }: { title: string; intro: ReactNode; children: ReactNode }) {
  return (
    <section className="form-section">
      <div className="form-section-head">
        <h2>{title}</h2>
        <p className="muted">{intro}</p>
      </div>
      <div className="client-form">{children}</div>
    </section>
  );
}

/**
 * Another client with this phone or email, found as the office types, so nobody is added twice (Jobber only catches it
 * later, with Merge clients). Null when there is none.
 */
function useTwin(companyId: string, phone: string, email: string, skip: string | null) {
  const digits = phone.replace(/\D/g, "").slice(-10);
  const mail = /^\S+@\S+\.\S+$/.test(email.trim()) ? email.trim().toLowerCase() : "";
  const byPhone = useClients(companyId, { status: "all", q: digits }, digits.length === 10);
  const byMail = useClients(companyId, { status: "all", q: mail }, Boolean(mail));
  const phoneTwin =
    digits.length === 10
      ? byPhone.data?.items.find((r) => r.id !== skip && (r.phone ?? "").replace(/\D/g, "").endsWith(digits))
      : undefined;
  if (phoneTwin) return { client: phoneTwin, by: "phone number" };
  const mailTwin = mail ? byMail.data?.items.find((r) => r.id !== skip && r.email?.toLowerCase() === mail) : undefined;
  return mailTwin ? { client: mailTwin, by: "email" } : null;
}

export function ClientForm({ id }: { id?: string }) {
  const existing = useClient(id ?? "");
  if (id && !existing.data)
    return <Page title="Edit client">{existing.error ? <LoadError error={existing.error} /> : <Skeleton rows={6} />}</Page>;
  return <ClientFormBody c={id ? existing.data : undefined} />;
}

/**
 * New and Edit client (2026-10-29). Jobber's page, section by section with the browser's autofill names, plus what neither
 * Jobber nor the old Steward did: a warning while typing when the phone or email is already a client's, tags picked from
 * the ones in use, and Save and add a job, so booking is the next step instead of a crowded form (the old Steward asked for
 * the job, price, checklist and billing all on this page).
 */
function ClientFormBody({ c }: { c: ClientDetail | undefined }) {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const create = useCreateClient();
  const update = useUpdateClient();
  const saveProperty = useSaveProperty();
  const summary = useClientSummary(company.id);
  const [f, setF] = useState({
    title: c?.title ?? "",
    firstName: c?.firstName ?? "",
    lastName: c?.lastName ?? "",
    companyName: c?.companyName ?? "",
    isCompany: c?.isCompany ?? false,
    phone: phoneText(c?.phone ?? null) ?? "",
    email: c?.email ?? "",
    leadSource: c?.leadSource ?? "",
    prefers: c?.prefers ?? "text",
    noMarketing: c?.noMarketing ?? false,
    officeNotes: c?.officeNotes ?? "",
    referredById: c?.referredBy?.id ?? "",
  });
  const [billOn, setBillOn] = useState(Boolean(c?.billing));
  const [billing, setBilling] = useState<AddressDraft>(() => draftOf(c?.billing));
  // Referred by asks for the client who sent them; the list loads only when it shows.
  const askReferrer = f.leadSource === "referral" || Boolean(f.referredById);
  const referrers = useClients(company.id, { status: "active" }, askReferrer);
  const [tags, setTags] = useState<string[]>(c?.tags ?? []);
  const [tagText, setTagText] = useState("");
  const [extras, setExtras] = useState<ContactDraft[]>(() => (c?.contacts ?? []).filter((x) => !x.isMain).map(contactDraftOf));
  // A saved client changes their one property's address here; with more than one, each is changed on their page.
  const home = c?.properties.length === 1 ? c.properties[0]! : null;
  const [placeStart] = useState<PlaceDraft>(() => (home ? placeOf(home) : EMPTY_PLACE));
  const [place, setPlace] = useState<PlaceDraft>(placeStart);
  const [error, setError] = useState<string | null>(null);
  const [then, setThen] = useState<"page" | "job">("page");
  /** Which save button was pressed; read in the submit, which runs before the state above updates. */
  const thenRef = useRef<"page" | "job">("page");
  const press = (v: "page" | "job") => {
    thenRef.current = v;
    setThen(v);
  };
  const twin = useTwin(company.id, f.phone, f.email, c?.id ?? null);
  const named = Boolean(f.companyName.trim());
  const business = named || f.isCompany;
  const set = (k: "firstName" | "lastName" | "companyName" | "phone" | "email" | "title") => ({
    value: f[k],
    onChange: (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value }),
  });
  const addTag = (t: string) => {
    const tag = t.trim().replace(/,$/, "").trim();
    if (tag && !tags.some((x) => x.toLowerCase() === tag.toLowerCase())) setTags([...tags, tag]);
    setTagText("");
  };
  const unusedTags = (summary.data?.tags ?? []).map((t) => t.tag).filter((t) => !tags.includes(t));
  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    const p = placeProperty(place);
    const bill = billOn ? partsOf(billing) : { parts: null, problem: null };
    const missing = c
      ? !f.firstName.trim() && !f.lastName.trim() && !f.companyName.trim()
        ? "Give a first or last name, or a company name."
        : home && !place.street1.trim()
          ? "Type the street address."
          : null
      : newClientMissing(f, { ...p, address: place.street1 });
    const more = contactsOut(extras);
    const problem = missing ?? (bill.problem ? `Billing address. ${bill.problem}` : null);
    if (problem || "error" in more) {
      setError(problem ?? ("error" in more ? more.error : null));
      return;
    }
    const allTags = tagText.trim() ? [...tags, tagText.trim()] : tags;
    const body = {
      ...(f.title ? { title: f.title as (typeof CLIENT_TITLES)[number] } : { title: null }),
      firstName: txt(f.firstName),
      lastName: txt(f.lastName),
      companyName: txt(f.companyName) ?? null,
      isCompany: business,
      phone: txt(f.phone),
      email: txt(f.email),
      tags: allTags,
      leadSource: (f.leadSource || undefined) as (typeof LEAD_SOURCES)[number] | undefined,
      prefers: f.prefers,
      noMarketing: f.noMarketing,
      officeNotes: txt(f.officeNotes),
      billing: bill.parts,
      referredById: f.referredById || null,
    };
    const done = (x: ClientDetail) => {
      toast(c ? "Client saved" : `${x.name} added`);
      if (!c) markFresh(`client:${x.id}`);
      if (thenRef.current === "job") void navigate({ to: "/jobs/new", search: { clientId: x.id } });
      else void navigate({ to: "/clients/$id", params: { id: x.id } });
    };
    const fail = (er: unknown) => setError(errText(er));
    // The address saves after the client: the one property changed, or a first one added to a client who had none.
    const placeChanged = JSON.stringify(place) !== JSON.stringify(placeStart) && Boolean(place.street1.trim());
    const thenPlace = (x: ClientDetail) =>
      c && placeChanged && (home || !c.properties.length)
        ? saveProperty.mutate(
            { clientId: c.id, ...(home ? { id: home.id } : {}), property: placeOut(place, home?.label ?? "", home) },
            { onSuccess: () => done(x), onError: fail },
          )
        : done(x);
    if (c) update.mutate({ id: c.id, patch: { ...body, contacts: more.contacts } }, { onSuccess: thenPlace, onError: fail });
    else
      create.mutate(
        {
          ...body,
          firstName: f.firstName.trim(),
          phone: f.phone.trim(),
          email: f.email.trim(),
          property: propertyOut(p),
          contacts: more.contacts,
        },
        { onSuccess: done, onError: fail },
      );
  };
  const saving = create.isPending || update.isPending || saveProperty.isPending;
  return (
    <Page title={c ? `Edit ${c.name}` : "New client"}>
      <form className="client-page-form pform" autoComplete="on" onSubmit={submit} aria-label={c ? "Edit client" : "New client"}>
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} onClick={() => press("page")} />
        {error ? <Banner type="error">{error}</Banner> : null}
        <FormSection title="Who they are" intro="The main person you talk to. Mark a business, with or without a company name.">
          <div className="span-2 wide-phone">
            <FormField label="Title">
              <select className="select" name="honorific-prefix" autoComplete="honorific-prefix" {...set("title")}>
                <option value="">No title</option>
                {CLIENT_TITLES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </FormField>
          </div>
          <div className="span-2 wide-phone">
            <InputText
              label="First name"
              name="given-name"
              autoComplete="given-name"
              autoFocus={!c}
              required={!c}
              {...set("firstName")}
            />
          </div>
          <div className="span-2 wide-phone">
            <InputText label="Last name" name="family-name" autoComplete="family-name" {...set("lastName")} />
          </div>
          <div className="span-6">
            <InputText
              label="Company name"
              name="organization"
              autoComplete="organization"
              description="Leave blank if the business has no name to show."
              {...set("companyName")}
            />
          </div>
          <label className="span-6 row">
            <Switch checked={business} disabled={named} onChange={(v) => setF({ ...f, isCompany: v })} label="Business client" />
            <span>
              {business
                ? named
                  ? "A business, since it has a company name. Invoices are due in 7 days."
                  : "A business. Invoices are due in 7 days."
                : "Turn on for a business, like an office, store or landlord."}
            </span>
          </label>
        </FormSection>
        <FormSection
          title="How to reach them"
          intro="Invoices and visit reminders go to the main phone and email. Add more, like an office line or a billing email."
        >
          <div className="span-3 wide-phone">
            <InputText label="Phone number" type="tel" name="tel" autoComplete="tel" required={!c} {...set("phone")} />
          </div>
          <div className="span-3 wide-phone">
            <InputText label="Email" type="email" name="email" autoComplete="email" {...set("email")} />
          </div>
          {twin ? (
            <div className="span-6">
              <Banner
                type="warning"
                action={
                  <Button
                    type="secondary"
                    size="small"
                    onClick={() => void navigate({ to: "/clients/$id", params: { id: twin.client.id } })}
                  >
                    Open {twin.client.name}
                  </Button>
                }
              >
                {twin.client.name} already has this {twin.by}
                {twin.client.active ? "" : " (not active)"}. Open their page so they are not added twice.
              </Banner>
            </div>
          ) : null}
          <ExtraContacts list={extras} set={setExtras} />
          <div className="span-3 wide-phone">
            <Choice
              label="They like to hear from you by"
              value={f.prefers}
              onChange={(v) => setF({ ...f, prefers: v })}
              options={[
                { value: "text", label: "Text" },
                { value: "email", label: "Email" },
              ]}
            />
          </div>
          <label className="span-6 row">
            <Switch checked={!f.noMarketing} onChange={(v) => setF({ ...f, noMarketing: !v })} label="Marketing" />
            <span>Send them offers and news. Turn off if they asked you to stop.</span>
          </label>
        </FormSection>
        <FormSection
          title="Where the work is"
          intro={
            !c
              ? "Their home or business. Add more places on their page later."
              : home || !c.properties.length
                ? "Their home or business, and where bills go."
                : "Their places, and where bills go."
          }
        >
          {c && c.properties.length > 1 ? (
            <div className="span-6 place-list">
              {c.properties.map((p) => (
                <div key={p.id} className="row">
                  <Icon name="pin" size={16} />
                  <span>
                    {p.label ? <b>{p.label} </b> : null}
                    {fullAddress(p.address, p)}
                  </span>
                </div>
              ))}
              <span className="description">Change each one on their page, under Properties.</span>
            </div>
          ) : (
            <PlaceFields p={place} set={setPlace} trade={company.trade} business={business} optional={Boolean(c && !home)} />
          )}
          <label className="span-6 row">
            <Switch checked={billOn} onChange={setBillOn} label="Bills go to another address" />
            <span>
              {billOn
                ? "Billing address. Invoices go here, not to the property."
                : "Turn on when invoices go somewhere other than the property, like a PO Box."}
            </span>
          </label>
          {billOn ? (
            <div className="span-6">
              <AddressFields value={billing} onChange={setBilling} kind="billing" />
            </div>
          ) : null}
        </FormSection>
        <FormSection title="About them" intro="How they found you, tags to group them, and a note only the office sees.">
          <div className="span-3 wide-phone">
            <Combobox
              label="How they found you"
              value={f.leadSource}
              onChange={(v) => setF({ ...f, leadSource: v })}
              options={LEAD_SOURCES.map((s) => ({ value: s, label: LEAD_WORDS[s] ?? s }))}
            />
          </div>
          {askReferrer ? (
            <div className="span-3 wide-phone">
              <Combobox
                label="Referred by"
                value={f.referredById}
                onChange={(v) => setF({ ...f, referredById: v })}
                options={(referrers.data?.items ?? [])
                  .filter((x) => x.id !== c?.id)
                  .map((x) => ({ value: x.id, label: x.name, ...(x.address ? { sub: x.address } : {}) }))}
              />
            </div>
          ) : null}
          <div className="span-3 wide-phone">
            <InputText
              label="Tags"
              placeholder="Type a tag, then Enter"
              autoComplete="off"
              value={tagText}
              onChange={(e) => (e.target.value.endsWith(",") ? addTag(e.target.value) : setTagText(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && tagText.trim()) {
                  e.preventDefault();
                  addTag(tagText);
                } else if (e.key === "Backspace" && !tagText && tags.length) setTags(tags.slice(0, -1));
              }}
            />
          </div>
          {tags.length || unusedTags.length ? (
            <div className="span-6 tag-picker">
              {tags.map((t) => (
                <button key={t} type="button" className="tag on" onClick={() => setTags(tags.filter((x) => x !== t))}>
                  {t} <Icon name="close" size={12} />
                </button>
              ))}
              {unusedTags.slice(0, 8).map((t) => (
                <button key={t} type="button" className="tag add" onClick={() => addTag(t)}>
                  <Icon name="plus" size={12} /> {t}
                </button>
              ))}
            </div>
          ) : null}
          <div className="span-6">
            <FormField label="Office note" description="Shows at the top of their page. The crew does not see it.">
              <textarea rows={3} value={f.officeNotes} onChange={(e) => setF({ ...f, officeNotes: e.target.value })} />
            </FormField>
          </div>
        </FormSection>
        <div className="form-foot form-bar">
          <Button type="tertiary" onClick={() => window.history.back()}>
            Cancel
          </Button>
          <div className="spacer" />
          {!c && can("job.update") ? (
            <Button type="secondary" submit loading={saving && then === "job"} onClick={() => press("job")}>
              Save and add a job
            </Button>
          ) : null}
          <Button submit loading={saving && then === "page"} onClick={() => press("page")}>
            Save client
          </Button>
        </div>
      </form>
    </Page>
  );
}
